/**
 * contentTypeMeta
 *
 * Single source for the course-type and lesson-type taxonomy chips —
 * label, themed chip color, and a leading icon. Previously copy-pasted
 * into ~10 components (catalog card, workspace/group course pickers,
 * curriculum builder, lesson viewers, activity/assignment lists) where
 * the copies had already begun to drift. Import these instead of
 * re-declaring the switch.
 *
 * The learner course-detail surface (`LearnerCourseDetailLayout`) keeps
 * its own variant on purpose — it uses friendlier labels ("Slides",
 * "Document"), brand-tinted colors, and an extra `tint` field, so it is
 * a deliberately different presentation rather than a copy to fold in.
 */
import React from 'react';
import { CourseType, LessonType } from '../../apis/api-client';
import {
  BookmarkIcon,
  FileTextIcon,
  OpenInNewIcon,
  PlayCircleIcon,
  QuizIcon,
  SchoolIcon,
} from '../icons';

export interface TypeMeta {
  label: string;
  icon: React.ReactNode;
  color: string;
}

// Most surfaces want MUI's `fontSize="small"` (~20px); the dense
// catalog card wants a smaller 13px glyph. Accept either form so the
// shared meta renders identically to each call site's prior inline copy.
type IconFontSize = number | 'small' | 'inherit' | 'medium' | 'large';

function iconProps(size: IconFontSize) {
  return typeof size === 'number' ? { sx: { fontSize: size } } : { fontSize: size };
}

export function courseTypeMeta(
  type: CourseType | undefined,
  iconFontSize: IconFontSize = 'small',
): TypeMeta {
  const ip = iconProps(iconFontSize);
  switch (type) {
    case CourseType.LearningPath:
      return { label: 'Learning Path', icon: <BookmarkIcon {...ip} />, color: '#6a1b9a' };
    case CourseType.External:
      return { label: 'Event', icon: <OpenInNewIcon {...ip} />, color: '#2e7d32' };
    default:
      return { label: 'Course', icon: <SchoolIcon {...ip} />, color: '#1565c0' };
  }
}

export function lessonTypeMeta(
  type: LessonType | undefined,
  iconFontSize: IconFontSize = 'small',
): TypeMeta {
  const ip = iconProps(iconFontSize);
  switch (type) {
    case LessonType.Video:
      return { label: 'Video', icon: <PlayCircleIcon {...ip} />, color: '#1565c0' };
    case LessonType.Quiz:
      return { label: 'Quiz', icon: <QuizIcon {...ip} />, color: '#6a1b9a' };
    case LessonType.PPT:
      return { label: 'PowerPoint', icon: <FileTextIcon {...ip} />, color: '#bf360c' };
    case LessonType.Word:
      return { label: 'Word', icon: <FileTextIcon {...ip} />, color: '#1565c0' };
    case LessonType.PDF:
      return { label: 'PDF', icon: <FileTextIcon {...ip} />, color: '#b71c1c' };
    case LessonType.Text:
      return { label: 'Text', icon: <FileTextIcon {...ip} />, color: '#37474f' };
    case LessonType.Scorm:
      return { label: 'SCORM', icon: <FileTextIcon {...ip} />, color: '#4527a0' };
    default:
      return { label: 'Lesson', icon: <FileTextIcon {...ip} />, color: '#37474f' };
  }
}
